import React, { useState } from 'react';
import { Settings, FileText, Code, BarChart3, Gavel, Radio } from 'lucide-react';
import { SavedServer } from '../types';
import ServerEditor from './ServerEditor';
import ServerLogs from './ServerLogs';
import JsonViewer from './JsonViewer';
import Results from './Results';
import Stewarding from './Stewarding';
import LiveData from './LiveData';
import './ContentTabs.css';

interface ContentTabsProps {
  server: SavedServer | null;
  isNew: boolean;
  existingPorts?: number[];
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
  onSave: (server: SavedServer) => void;
  onClose?: () => void;
  onRefreshServer?: (serverId: string) => Promise<void> | void;
}

type TabType = 'config' | 'logs' | 'json' | 'results' | 'stewarding' | 'live';

const ContentTabs: React.FC<ContentTabsProps> = ({ server, isNew, existingPorts, activeTab: activeTabProp, onTabChange, onSave, onClose, onRefreshServer }) => {
  const [internalTab, setInternalTab] = useState<TabType>('config');
  const activeTab = activeTabProp ?? internalTab;
  const setActiveTab = (tab: TabType) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  };

  const handleCancel = () => {
    setActiveTab('config');
    onClose?.();
  };

  if (!server && !isNew) {
    return <div className="content-tabs"><div className="no-selection">Select a server to configure</div></div>;
  }

  return (
    <div className="content-tabs">
      <div className="tabs-header-wrapper">
      <div className="tabs-header">
        <button
          className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <Settings size={16} />
          Configuration
        </button>
        {server && !isNew && (
          <>
            {server.status === 'running' && (
              <button
                className={`tab-button ${activeTab === 'live' ? 'active' : ''}`}
                onClick={() => setActiveTab('live')}
              >
                <Radio size={16} />
                Live
              </button>
            )}
            <button
              className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              <FileText size={16} />
              Logs
            </button>
            <button
              className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              <BarChart3 size={16} />
              Results
            </button>
            <button
              className={`tab-button ${activeTab === 'stewarding' ? 'active' : ''}`}
              onClick={() => setActiveTab('stewarding')}
            >
              <Gavel size={16} />
              Stewarding
            </button>
            <button
              className={`tab-button ${activeTab === 'json' ? 'active' : ''}`}
              onClick={() => setActiveTab('json')}
            >
              <Code size={16} />
              JSON
            </button>
          </>
        )}
      </div>
      </div>

      <div className="tabs-content">
        {activeTab === 'config' && (
          <ServerEditor
            server={server}
            isNew={isNew}
            existingPorts={existingPorts}
            onSave={onSave}
            onCancel={handleCancel}
            onRefreshServer={onRefreshServer}
          />
        )}

        {activeTab === 'logs' && server && (
          <ServerLogs serverId={server.id} />
        )}

        {activeTab === 'results' && server && (
          <Results serverId={server.id} />
        )}

        {activeTab === 'stewarding' && server && (
          <Stewarding server={server} />
        )}

        {activeTab === 'json' && server && (
          <JsonViewer server={server} onSave={onSave} />
        )}

        {activeTab === 'live' && server && (
          <LiveData serverId={server.id} serverStatus={server.status} />
        )}
      </div>
    </div>
  );
};

export default ContentTabs;
